import React from "react";

import { faPlus } from "@performyard/fontawesome/pro-regular-svg-icons";
import { ButtonStandard } from "@performyard/py-components";

import ChecklistItem from "./ChecklistItem";
import ChecklistSection from "./ChecklistSection";

import type { AssignmentClickArgs, ItemStatus } from "../types";

import { useAddMeetingActionItem, useEditMeetingActionItem, useMeetingActionItems } from "../../../../api/hooks";
import { useChecklistItems } from "../hooks";

import { useMeetingContext } from "../context";

/**
 * Action items section with the ability to add, edit, delete, and assign action items.
 */
const MeetingDetailsActionItems = (): React.ReactElement => {
	const { meeting, setUserAssignment, isInteractionDisabled, userId, workspace } = useMeetingContext();
	const { data: items, isLoading } = useMeetingActionItems(meeting._id);
	const { mutate: addItem } = useAddMeetingActionItem();
	const { mutate: editItem } = useEditMeetingActionItem();

	const {
		activeItems,
		isAddingItem,
		newItemName,
		editingItemId,
		editingItemCurrentValue,
		handleEditStart,
		handleEditStop,
		handleNameChange,
		handleNewItemNameChange,
		handleStatusChange,
		handleAddButtonClick,
		handleNewItemSave,
	} = useChecklistItems({
		meetingId: meeting._id,
		itemType: "action",
		items,
		isLoading,
		addItemMutation: addItem,
		editItemMutation: editItem,
		isInteractionDisabled,
	});

	const handleAssignmentClick = (args: AssignmentClickArgs): void => {
		if (isInteractionDisabled) return;

		setUserAssignment({
			actionItemId: args.itemId,
			meetingId: args.meetingId,
			currentAssigneeId: args.currentAssigneeId,
			dueDate: args.dueDate,
			name: args.name,
		});
	};

	const isAddButtonDisabled =
		isInteractionDisabled || editingItemId !== null || (isAddingItem && !newItemName.trim());

	const addButton = !isInteractionDisabled ? (
		<ButtonStandard
			iconLeft={faPlus}
			onClick={handleAddButtonClick}
			disabled={isAddButtonDisabled}
			dangerouslySetClassName={
				isAddButtonDisabled
					? "disabled:cursor-not-allowed disabled:pointer-events-auto w-fit flex-shrink-0"
					: "w-fit flex-shrink-0"
			}
		>
			Add Action Item
		</ButtonStandard>
	) : undefined;

	return (
		<ChecklistSection
			title="Action Items"
			isLoading={isLoading}
			errorMessage="Failed to load action items"
			variant="main"
			footer={addButton}
		>
			{activeItems?.map((item) => {
				const meetingItem = item.meetings.find((m) => m.meetingId === meeting._id);
				const assignee = item.participants?.find((p) => p.role === "assignee");
				const assigneeName = assignee?.userId
					? workspace?.participants?.find((p) => p.userId === assignee.userId)?.name
					: undefined;

				return (
					<ChecklistItem
						key={item._id}
						id={item._id}
						name={item.name}
						dueDate={item.dueDate ? new Date(item.dueDate) : null}
						status={(meetingItem?.status as ItemStatus) || "incomplete"}
						creatorId={item.created?.userId || ""}
						userId={userId}
						isEditing={editingItemId === item._id}
						assigneeId={assignee?.userId}
						assigneeName={assigneeName}
						meetingId={meeting._id}
						itemType="action"
						onEdit={handleEditStart}
						onNameChange={handleNameChange}
						onStatusChange={(status) => handleStatusChange(item._id, status)}
						onSave={handleEditStop}
						currentEditValue={editingItemCurrentValue}
						onAssignmentClick={handleAssignmentClick}
					/>
				);
			})}

			{isAddingItem && (
				<ChecklistItem
					id="new-action-item"
					name=""
					status="incomplete"
					creatorId={userId}
					userId={userId}
					isEditing={true}
					isNewItem={true}
					itemType="action"
					onEdit={() => {}}
					onNameChange={handleNewItemNameChange}
					onStatusChange={() => {}}
					onSave={handleNewItemSave}
					currentEditValue={newItemName}
				/>
			)}
		</ChecklistSection>
	);
};

export default MeetingDetailsActionItems;
